import * as React from 'react'

/**
 * Has anything been typed since this editor opened?
 *
 * Pass a plain snapshot of the form's state — strings, numbers, booleans, and
 * arrays of those. The first render's snapshot is the baseline, which is right
 * for every editor here: they all mount with their values already seeded from
 * the row (or from empty, for a new one) and are torn down on save.
 *
 * A `Set` or a `Map` serializes as `{}`, so spread it at the call site
 * (`picked: [...picked]`). Order matters to the comparison, which is fine for
 * the lists these forms hold — they are rendered in a stable order — and is the
 * reason to spread a `Set` rather than sort it: a reordered answer is still a
 * change worth warning about.
 */
export function useDirty(snapshot: unknown): boolean {
  const current = JSON.stringify(snapshot)
  const initial = React.useRef(current)
  return initial.current !== current
}

export interface DiscardGuard {
  /** True while the "discard?" question is on screen. */
  asking: boolean
  /** There is something to lose. Drives the browser's own unload prompt. */
  dirty: boolean
  /** What every close path calls: Cancel, Escape, the X, a click outside. */
  requestClose: () => void
  /**
   * The same question, for a close path that goes somewhere other than out —
   * a form nested inside a dialog whose Cancel returns to the list behind it
   * rather than shutting the dialog.
   */
  requestCloseTo: (to: () => void) => void
  /** Confirmed: throw the edits away and close. */
  discard: () => void
  /** Changed their mind: put the question away and leave the editor open. */
  keepEditing: () => void
}

/**
 * Closing an editor must not silently destroy what is in it.
 *
 * Radix closes a dialog on Escape and on a click outside, and every editor in
 * this app wired that straight to `onClose()` — so a coach typing a 1:1 note
 * mid-conversation lost it by clicking the page behind the dialog. Coaching
 * notes are the worst case (nobody else can even read them, so nothing can be
 * reconstructed), but the same hand slips in the topic editor, the workshop
 * notes and a report section.
 *
 * Two covers, because there are two ways out:
 *
 * - **Inside the app** — `requestClose()` asks first when dirty, and the
 *   `<DiscardPrompt>` rendered in the dialog is the question.
 * - **Out of the app** — a reload, a closed tab, the back button to another
 *   origin. Only `beforeunload` reaches those, and the browser owns the
 *   wording; our text is ignored by every current browser and is supplied
 *   anyway for the old ones that read it.
 *
 * The listener is registered ONLY while dirty. A permanent one makes every
 * reload of a page nobody has touched ask a question, which teaches people to
 * dismiss the question.
 */
export function useDiscardGuard(dirty: boolean, close: () => void): DiscardGuard {
  const [asking, setAsking] = React.useState(false)

  React.useEffect(() => {
    if (!dirty) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  // Where a confirmed discard goes. Written when the question is asked, so one
  // guard can serve both "close the dialog" and "back out of the nested form".
  const pending = React.useRef(close)

  const requestCloseTo = React.useCallback(
    (to: () => void) => {
      if (dirty) {
        pending.current = to
        setAsking(true)
      } else {
        to()
      }
    },
    [dirty],
  )

  const requestClose = React.useCallback(() => requestCloseTo(close), [requestCloseTo, close])

  const discard = React.useCallback(() => {
    setAsking(false)
    pending.current()
  }, [])

  const keepEditing = React.useCallback(() => setAsking(false), [])

  return { asking, dirty, requestClose, requestCloseTo, discard, keepEditing }
}

/**
 * Commit a field's value when it loses focus — AND when it is unmounted still
 * holding an uncommitted edit.
 *
 * `onBlur` alone has a hole that looks like data loss and reads like a bug in
 * the save: removing a focused element from the DOM does not fire `blur` in any
 * browser. So an in-place note field that commits on blur loses the edit if the
 * thing that takes the field away is a route change, a tab switch or a refetch
 * that re-keys it — exactly the moments someone types a sentence and moves on.
 *
 * `commit` is held in a ref so the cleanup runs the latest closure without
 * re-registering on every keystroke.
 */
export function useCommitOnUnmount(commit: () => void): void {
  const latest = React.useRef(commit)
  latest.current = commit
  React.useEffect(() => () => latest.current(), [])
}
